import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className="min-h-screen relative overflow-x-hidden text-white bg-[#0c0915]">
            {/* Background Image */}
            <div
                className="fixed inset-0 z-0 bg-cover bg-center bg-no-repeat pointer-events-none"
                style={{ backgroundImage: 'url("/mainBgImg.png")' }}
            />

            {/* Header */}
            <header className="relative z-10 pt-8 sm:pt-12 pb-6 px-4 sm:px-6 lg:px-20 max-w-[1000px] mx-auto">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-[#DCCBFD] hover:text-white transition-colors mb-8"
                    style={{ fontFamily: "'Poppins', sans-serif", fontSize: "14px" }}
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back to Home
                </Link>

                <h1
                    className="text-white"
                    style={{
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: "clamp(28px, 6vw, 48px)",
                        lineHeight: "clamp(36px, 7vw, 58px)",
                    }}
                >
                    Privacy Policy
                </h1>
                <p
                    className="text-[#DCCBFD] mt-3"
                    style={{
                        fontFamily: "'Roboto', sans-serif",
                        fontWeight: 400,
                        fontSize: "clamp(13px, 2.5vw, 15px)",
                    }}
                >
                    Last updated: February 10, 2026
                </p>
            </header>

            {/* Content */}
            <main className="relative z-10 px-4 sm:px-6 lg:px-20 pb-16 sm:pb-24 max-w-[1000px] mx-auto">
                <div className="space-y-10">

                    {/* Introduction */}
                    <Section title="Introduction">
                        <p>
                            Wellcrafted Technologies ("we," "our," or "us") operates the Eddy AI mobile application and website (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use Eddy AI.
                        </p>
                        <p>
                            By accessing or using Eddy AI, you agree to this Privacy Policy. If you do not agree with the terms of this policy, please do not use the Service.
                        </p>
                    </Section>

                    {/* Information We Collect */}
                    <Section title="Information We Collect">
                        <h4 className="text-white font-semibold mb-2" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "clamp(14px, 3vw, 16px)" }}>
                            1. Information You Provide
                        </h4>
                        <ul className="list-disc list-inside space-y-2 ml-2">
                            <li><strong className="text-white">Account Information:</strong> Name, email address, and password when you create an account.</li>
                            <li><strong className="text-white">Scanned Content:</strong> Photos of textbook pages, handwritten notes, equations, graphs, diagrams, and PDFs that you upload or scan using your device camera.</li>
                            <li><strong className="text-white">Typed Input:</strong> Questions and study material you manually enter into the app.</li>
                            <li><strong className="text-white">Study Material:</strong> Notes, folders, and organized content you create and save within the app.</li>
                            <li><strong className="text-white">Payment Information:</strong> If you subscribe to a Premium plan, payment details are processed securely through third-party payment providers (Apple App Store or Google Play Store). We do not store your credit card information.</li>
                        </ul>

                        <h4 className="text-white font-semibold mb-2 mt-6" style={{ fontFamily: "'Poppins', sans-serif", fontSize: "clamp(14px, 3vw, 16px)" }}>
                            2. Information Collected Automatically
                        </h4>
                        <ul className="list-disc list-inside space-y-2 ml-2">
                            <li><strong className="text-white">Device Information:</strong> Device type, operating system, unique device identifiers, and mobile network information.</li>
                            <li><strong className="text-white">Usage Data:</strong> Features you use, actions you take, time and duration of your activities, and interaction patterns.</li>
                            <li><strong className="text-white">Learning Analytics:</strong> Quiz performance, study progress, strengths, areas for improvement, and learning patterns to personalize your experience.</li>
                            <li><strong className="text-white">Camera Access Data:</strong> When you use the scan feature, we access your device camera solely to capture the content you direct at it. We do not continuously access or monitor your camera.</li>
                        </ul>
                    </Section>

                    {/* How We Use Your Information */}
                    <Section title="How We Use Your Information">
                        <ul className="list-disc list-inside space-y-2 ml-2">
                            <li>Process and analyze your scanned questions to provide step-by-step solutions.</li>
                            <li>Generate smart notes, summaries, and quizzes from your study material.</li>
                            <li>Personalize your learning experience by adapting quiz difficulty and content recommendations based on your performance.</li>
                            <li>Organize and store your study materials in subject folders.</li>
                            <li>Enable sharing and collaboration features with classmates.</li>
                            <li>Improve and optimize our AI algorithms and Service quality.</li>
                            <li>Communicate with you about updates, new features, and support.</li>
                            <li>Process subscriptions and manage your account.</li>
                            <li>Ensure the security and integrity of our Service.</li>
                        </ul>
                    </Section>

                    {/* AI Processing */}
                    <Section title="AI Processing">
                        <p>
                            Eddy AI uses artificial intelligence to analyze your uploaded content and generate solutions, notes, and quizzes. Your scanned images and text are processed by our AI systems to deliver accurate, step-by-step explanations. We may use aggregated and anonymized data to improve our AI models, but we do not use your personally identifiable content to train AI models shared with third parties.
                        </p>
                    </Section>

                    {/* Data Storage and Security */}
                    <Section title="Data Storage and Security">
                        <p>
                            We use Firebase, a Google Cloud platform, for authentication and data storage. Your data is encrypted in transit and at rest. We implement industry-standard security measures to protect your information, including:
                        </p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li>SSL/TLS encryption for all data transmissions.</li>
                            <li>Secure authentication protocols.</li>
                            <li>Regular security audits and updates.</li>
                            <li>Access controls limiting who can view your data within our organization.</li>
                        </ul>
                        <p className="mt-3">
                            While we strive to protect your information, no method of electronic transmission or storage is 100% secure. We cannot guarantee absolute security.
                        </p>
                    </Section>

                    {/* Data Sharing */}
                    <Section title="Data Sharing and Disclosure">
                        <p>We do not sell your personal information. We may share your data in the following circumstances:</p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li><strong className="text-white">Service Providers:</strong> With trusted third-party providers who help us operate the Service (e.g., Firebase for hosting, cloud processing services).</li>
                            <li><strong className="text-white">Collaboration Features:</strong> When you choose to share notes or study materials with other users.</li>
                            <li><strong className="text-white">Legal Requirements:</strong> When required by law, regulation, or legal process.</li>
                            <li><strong className="text-white">Safety:</strong> To protect the rights, safety, or property of Wellcrafted Technologies, our users, or others.</li>
                            <li><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your data may be transferred as part of that transaction.</li>
                        </ul>
                    </Section>

                    {/* Data Retention */}
                    <Section title="Data Retention">
                        <p>
                            We retain your personal information for as long as your account is active or as needed to provide you with the Service. You may request deletion of your account and associated data at any time. Upon account deletion, we will remove your personal data within 30 days, except where retention is required by law.
                        </p>
                    </Section>

                    {/* Your Rights */}
                    <Section title="Your Rights">
                        <p>Depending on your location, you may have the following rights:</p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li><strong className="text-white">Access:</strong> Request a copy of the personal data we hold about you.</li>
                            <li><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete data.</li>
                            <li><strong className="text-white">Deletion:</strong> Request deletion of your personal data.</li>
                            <li><strong className="text-white">Data Portability:</strong> Request your data in a structured, machine-readable format.</li>
                            <li><strong className="text-white">Withdraw Consent:</strong> Withdraw consent for data processing at any time.</li>
                            <li><strong className="text-white">Opt-Out:</strong> Opt out of marketing communications.</li>
                        </ul>
                        <p className="mt-3">
                            To exercise any of these rights, please contact us at the email address provided below.
                        </p>
                    </Section>

                    {/* Children's Privacy */}
                    <Section title="Children's Privacy">
                        <p>
                            Eddy AI is designed for students of all ages. If you are under 13 (or under the applicable age of digital consent in your jurisdiction), you must have parental or guardian consent to use the Service. We do not knowingly collect personal information from children without such consent. If we learn we have collected data from a child without proper consent, we will promptly delete it.
                        </p>
                    </Section>

                    {/* Third-Party Services */}
                    <Section title="Third-Party Services">
                        <p>Our Service may contain links to or integrate with third-party services, including:</p>
                        <ul className="list-disc list-inside space-y-2 ml-2 mt-3">
                            <li>Apple App Store and Google Play Store (for app distribution and payments).</li>
                            <li>Firebase / Google Cloud (for authentication and data storage).</li>
                            <li>Social media platforms (Facebook, Twitter, LinkedIn, Instagram).</li>
                        </ul>
                        <p className="mt-3">
                            These third-party services have their own privacy policies, and we encourage you to review them. We are not responsible for the privacy practices of third-party services.
                        </p>
                    </Section>

                    {/* Changes to Policy */}
                    <Section title="Changes to This Privacy Policy">
                        <p>
                            We may update this Privacy Policy from time to time. We will notify you of material changes by posting the updated policy within the app or by email. Your continued use of the Service after any changes constitutes your acceptance of the updated policy.
                        </p>
                    </Section>

                    {/* Contact */}
                    <Section title="Contact Us">
                        <p>If you have questions or concerns about this Privacy Policy, please contact us:</p>
                        <div className="mt-3 space-y-1">
                            <p><strong className="text-white">Wellcrafted Technologies</strong></p>
                            <p>Email: contact@wellcraftedtech.com</p>
                            <p>Website: <a href="https://www.wellcraftedtech.com" className="text-[#8E57FA] hover:underline" target="_blank" rel="noopener noreferrer">www.wellcraftedtech.com</a></p>
                        </div>
                    </Section>

                </div>
            </main>
        </div>
    );
};

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div>
        <h3
            className="text-white mb-4"
            style={{
                fontFamily: "'Poppins', sans-serif",
                fontWeight: 600,
                fontSize: "clamp(18px, 4vw, 24px)",
                lineHeight: "clamp(26px, 5vw, 32px)",
            }}
        >
            {title}
        </h3>
        <div
            className="text-[#DCCBFD] space-y-3"
            style={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 400,
                fontSize: "clamp(14px, 2.8vw, 16px)",
                lineHeight: "clamp(22px, 4vw, 28px)",
            }}
        >
            {children}
        </div>
    </div>
);

export default PrivacyPolicy;
